import React, { useEffect, useState } from "react";
import { IEvent } from "../../../interfaces/models/IEvent";
import ChevronRight from "../../Atomos/Icons/ChevronRight";
import { UseFirmList } from "../../../hooks/Catalogs/useFirm";
import { UseGroupList } from "../../../hooks/Catalogs/useGroupList";
import ColorLogo from "../../Layout/Logo/ColorLogo";
import inPersonImage from "../../../assets/images&icons/in-person.jpg";
import virtualImage from "../../../assets/images&icons/virtual.jpg";
import hybridImage from "../../../assets/images&icons/hibrid.jpg";
import { formatEventLocation } from "../../../utils/eventLocationFormatter";
import { formatEventDate } from "../../../utils/eventDateFormatter";

interface EventCardProps {
  event: IEvent;
  onDetailsClick?: (event: IEvent) => void;
  firmData?: any[];
  groupData?: any[];
}

const EventCardView: React.FC<
  EventCardProps & { firmData: any[]; groupData: any[] }
> = ({ event, onDetailsClick, firmData, groupData }) => {
  const [firmLogos, setFirmLogos] = useState<
    {
      id: string;
      logo: string | null;
      smallLogo?: string | null;
      name: string;
    }[]
  >([]);
  const [groupLogos, setGroupLogos] = useState<
    {
      id: string;
      logo: string | null;
      smallLogo?: string | null;
      name: string;
    }[]
  >([]);
  const [showWSGLogo, setShowWSGLogo] = useState(false);
  const [groupPhotoFallback, setGroupPhotoFallback] = useState<string | null>(
    null
  );
  const [practiceIndustryLogo, setPracticeIndustryLogo] = useState<any[]>([]);

  useEffect(() => {
    if (event.setupType === "group" || event.setupType === "groupWebinar") {
      const matchedGroups = groupData.filter((g: any) =>
        event.groupIds?.includes(g.title)
      );

      const uniqueGroupsMap = new Map<string, any>();
      matchedGroups.forEach((g: any) => {
        if (g.title && !uniqueGroupsMap.has(g.title)) {
          uniqueGroupsMap.set(g.title, {
            id: g.id,
            logo: g.logo,
            name: g.title,
          });
        }
      });
      setPracticeIndustryLogo(Array.from(uniqueGroupsMap.values()));
    }

    if (
      event.eventHostSponsor &&
      event.eventHostSponsor.toLowerCase().includes("wsg")
    ) {
      setShowWSGLogo(true);
    } else {
      setShowWSGLogo(false);
    }

    if (event.sponsorHost && event.sponsorHost.length > 0) {
      const memberSponsors = event.sponsorHost.filter(
        (sponsor) => sponsor.type === "member"
      );

      const groupSponsors = event.sponsorHost.filter(
        (sponsor: any) => sponsor.type === "wsg_group"
      );

      if (memberSponsors.length > 0) {
        if (firmData.length > 0) {
          const logos = memberSponsors.map((sponsor) => {
            const firm = firmData.find((f: any) => f.id === sponsor.id);
            return firm
              ? {
                  id: firm.id,
                  logo: firm.logo,
                  smallLogo: firm.smallLogo,
                  name: firm.name || firm.firmName,
                }
              : {
                  id: sponsor.id,
                  logo: null,
                  smallLogo: null,
                  name: "Unknown Firm",
                };
          });
          setFirmLogos(logos);
        } else {
          const placeholders = memberSponsors.map((sponsor) => ({
            id: sponsor.id,
            logo: null,
            smallLogo: null,
            name: "Loading...",
          }));
          setFirmLogos(placeholders);
        }
      } else {
        setFirmLogos([]);
      }

      if (groupSponsors.length > 0) {
        if (groupData.length > 0) {
          const logos = groupSponsors
            .map((sponsor: any) => {
              const group = groupData.find((g: any) => g.id === sponsor.id);
              return group && group.title
                ? {
                    id: group.id || sponsor.id,
                    logo: group.logo || null,
                    smallLogo: group.logo || null,
                    name: group.title,
                  }
                : null;
            })
            .filter((logo): logo is NonNullable<typeof logo> => logo !== null);
          setGroupLogos(logos);

          if (!event.bannerLists) {
            const firstSponsor = groupSponsors[0];
            const firstGroup = groupData.find(
              (g: any) => g.id === firstSponsor.id
            );
            if (firstGroup && firstGroup.photo) {
              setGroupPhotoFallback(firstGroup.photo);
            } else {
              setGroupPhotoFallback(null);
            }
          } else {
            setGroupPhotoFallback(null);
          }
        } else {
          const placeholders = groupSponsors.map((sponsor: any) => ({
            id: sponsor.id,
            logo: null,
            smallLogo: null,
            name: "Loading...",
          }));
          setGroupLogos(placeholders);
          setGroupPhotoFallback(null);
        }
      } else {
        if (
          event.sponsorHost &&
          event.sponsorHost.length > 0 &&
          groupData.length > 0
        ) {
          if (!event.bannerLists) {
            const firstGroup = groupData.find(
              (g: any) => g.title === event.sponsorHost![0].id
            );
            if (firstGroup && firstGroup.photo) {
              setGroupPhotoFallback(firstGroup.photo);
            } else {
              setGroupPhotoFallback(null);
            }
          } else {
            setGroupPhotoFallback(null);
          }
        } else {
          setGroupPhotoFallback(null);
        }
      }
    } else {
      setFirmLogos([]);
      setGroupLogos([]);
      setGroupPhotoFallback(null);
    }
  }, [
    event.sponsorHost,
    event.eventHostSponsor,
    event.setupType,
    event.groupIds,
    firmData,
    groupData,
    event.bannerLists,
  ]);

  const handleDetailsClick = () => {
    if (onDetailsClick) {
      onDetailsClick(event);
    }
  };

  const getDefaultEventImage = () => {
    if (event.eventType === "virtual") {
      return virtualImage;
    } else if (event.eventType === "hybrid") {
      return hybridImage;
    } else if (event.eventType === "inPerson") {
      return inPersonImage;
    }
    return inPersonImage;
  };

  return (
    <div className="bg-white overflow-hidden border-b pb-10 border-gray-200">
      <div className="flex flex-col md:flex-row gap-8">
        <div className="event-image-container self-stretch w-full md:w-[200px] md:h-[232px] h-[317px] flex-shrink-0 relative overflow-hidden">
          {event.bannerLists || groupPhotoFallback ? (
            <figure className="w-full h-full relative m-0">
              <img
                src={event.bannerLists || groupPhotoFallback || ""}
                alt={event.title || "Event Image"}
                className="w-full h-full object-contain md:object-cover rounded-[4px]"
                onError={(e) => {
                  if (
                    event.bannerLists &&
                    groupPhotoFallback &&
                    e.currentTarget.src === event.bannerLists
                  ) {
                    e.currentTarget.src = groupPhotoFallback;
                  } else {
                    e.currentTarget.src = getDefaultEventImage();
                  }
                }}
              />
            </figure>
          ) : (
            <figure className="w-full h-full relative m-0">
              <img
                src={getDefaultEventImage()}
                alt={event.title || "Event Image"}
                className="w-full h-full object-contain md:object-cover rounded-[4px]"
              />
            </figure>
          )}
        </div>

        <div className="flex-1 md:w-[62.5%] flex flex-col">
          <div className="flex flex-col xl:flex-row xl:justify-between h-full gap-4">
            <div className="flex-1 flex flex-col justify-between h-full">
              <div>
                <h3 className="text-gray-font text-left font-sans font-semibold text-[1.5rem] leading-[1.3] mb-[0.6rem]">
                  {event.title || "Event Title"}
                </h3>
                <p className="md:text-base text-sm leading-[18px] font-semibold text-Default mb-2 text-left">
                  {formatEventDate(event)}
                  {
                    <>
                      <span className="text-Default"> • </span>
                      {formatEventLocation(event)}
                    </>
                  }
                </p>
                <p className="text-gray-font mb-4 text-left lg:text-base text-sm font-normal lg:leading-6 leading-5">
                  {event.description || "No description available"}
                </p>
              </div>

              <div className="flex items-center gap-[10px] mt-auto">
                <button
                  className="group text-accent-Default-dark hover:text-primary text-sm font-bold all-small-caps align-middle font-decimal flex items-center gap-2 transition-colors relative"
                  onClick={handleDetailsClick}
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <span className="relative inline-block">
                    DETAILS
                    <span
                      className="absolute bottom-0 left-0 w-0 h-[2px] bg-primary transition-all duration-300 ease-out group-hover:w-full"
                      style={{
                        transformOrigin: "left",
                      }}
                    />
                  </span>
                  <div className="w-5 h-5 bg-Default group-hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                    <ChevronRight className="w-3 h-3 fill-white" />
                  </div>
                </button>
              </div>
            </div>

            <div className="xl:w-[29%] xl:ml-6 flex-shrink-0 xl:block hidden min-w-0">
              <p className="md:text-xl text-lg leading-normal font-semibold text-accent-Default-dark pb-5">
                Hosted by:
              </p>
              <div className="space-y-2 w-full">
                {practiceIndustryLogo.length > 0 ? (
                  practiceIndustryLogo.map((pi, index) => (
                    <div
                      key={`practice-industry-${index}`}
                      className="flex h-16 w-[220px] min-w-0 shrink-0 items-center justify-center overflow-hidden"
                    >
                      {pi.logo && (
                        <img
                          src={pi.logo || ""}
                          alt={pi.name ?? ""}
                          className="h-full w-full min-h-0 min-w-0 object-contain"
                        />
                      )}
                    </div>
                  ))
                ) : (
                  <>
                    {showWSGLogo && (
                      <div
                        key="wsg-logo"
                        className="flex h-16 w-[220px] min-w-0 shrink-0 items-center justify-center overflow-hidden"
                      >
                        <div className="flex h-full w-full min-h-0 min-w-0 items-center justify-center text-primary">
                          <ColorLogo />
                        </div>
                      </div>
                    )}
                    {firmLogos.length > 0
                      ? firmLogos.map((firm, index) => (
                          <div
                            key={`firm-${index}`}
                            className="flex h-16 w-[220px] min-w-0 shrink-0 items-center justify-center overflow-hidden"
                          >
                            {firm.smallLogo || firm.logo ? (
                              <img
                                src={firm.smallLogo || firm.logo || ""}
                                alt={firm.name}
                                className="h-full w-full min-h-0 min-w-0 object-contain"
                                onError={(e) => {
                                  if (
                                    firm.smallLogo &&
                                    firm.logo &&
                                    e.currentTarget.src !== firm.logo
                                  ) {
                                    e.currentTarget.src = firm.logo;
                                  } else {
                                    e.currentTarget.style.display = "none";
                                    e.currentTarget.nextElementSibling?.classList.remove(
                                      "hidden"
                                    );
                                  }
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-full h-full bg-gray-100 rounded flex items-center justify-center ${
                                firm.smallLogo || firm.logo ? "hidden" : ""
                              }`}
                            >
                              <span className="text-xs text-gray-500 text-center px-1">
                                {firm.name}
                              </span>
                            </div>
                          </div>
                        ))
                      : null}

                    {groupLogos.length > 0
                      ? groupLogos.map((group, index) => (
                          <div
                            key={`group-${index}`}
                            className="flex h-16 w-[220px] min-w-0 shrink-0 items-center justify-center overflow-hidden"
                          >
                            {group.smallLogo || group.logo ? (
                              <img
                                src={group.smallLogo || group.logo || ""}
                                alt={group.name}
                                className="h-full w-full min-h-0 min-w-0 object-contain"
                                onError={(e) => {
                                  if (
                                    group.smallLogo &&
                                    group.logo &&
                                    e.currentTarget.src !== group.logo
                                  ) {
                                    e.currentTarget.src = group.logo;
                                  } else {
                                    e.currentTarget.style.display = "none";
                                    e.currentTarget.nextElementSibling?.classList.remove(
                                      "hidden"
                                    );
                                  }
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-full h-full bg-gray-100 rounded flex items-center justify-center ${
                                group.smallLogo || group.logo ? "hidden" : ""
                              }`}
                            >
                              <span className="text-xs text-gray-500 text-center px-1">
                                {group.name}
                              </span>
                            </div>
                          </div>
                        ))
                      : null}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 xl:hidden text-left">
        <p className="md:text-xl text-lg leading-normal font-semibold text-accent-Default-dark mb-2">
          Hosted by:
        </p>
        <div className="flex w-full flex-wrap gap-2">
          {practiceIndustryLogo.length > 0 ? (
            practiceIndustryLogo.map((pi, index) => (
              <div
                key={`practice-industry-${index}`}
                className="flex h-16 w-[220px] min-w-0 shrink-0 items-center justify-center overflow-hidden"
              >
                {pi.logo && (
                  <img
                    src={pi.logo || ""}
                    alt={pi.name ?? ""}
                    className="h-full w-full min-h-0 min-w-0 object-contain"
                  />
                )}
              </div>
            ))
          ) : (
            <>
              {showWSGLogo && (
                <div
                  key="wsg-logo-mobile"
                  className="flex h-16 w-[220px] min-w-0 shrink-0 items-center justify-center overflow-hidden"
                >
                  <div className="flex h-full w-full min-h-0 min-w-0 items-center justify-center text-primary">
                    <ColorLogo />
                  </div>
                </div>
              )}
              {firmLogos.length > 0
                ? firmLogos.map((firm, index) => (
                    <div
                      key={`firm-${index}`}
                      className="flex h-16 w-[220px] min-w-0 shrink-0 items-center justify-center overflow-hidden"
                    >
                      {firm.smallLogo || firm.logo ? (
                        <img
                          src={firm.smallLogo || firm.logo || ""}
                          alt={firm.name}
                          className="h-full w-full min-h-0 min-w-0 object-contain"
                          onError={(e) => {
                            if (
                              firm.smallLogo &&
                              firm.logo &&
                              e.currentTarget.src !== firm.logo
                            ) {
                              e.currentTarget.src = firm.logo;
                            } else {
                              e.currentTarget.style.display = "none";
                              e.currentTarget.nextElementSibling?.classList.remove(
                                "hidden"
                              );
                            }
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-full h-full bg-gray-100 rounded flex items-center justify-center ${
                          firm.smallLogo || firm.logo ? "hidden" : ""
                        }`}
                      >
                        <span className="text-xs text-gray-500 text-center px-1">
                          {firm.name}
                        </span>
                      </div>
                    </div>
                  ))
                : null}

              {groupLogos.length > 0
                ? groupLogos.map((group, index) => (
                    <div
                      key={`group-${index}`}
                      className="flex h-16 w-[220px] min-w-0 shrink-0 items-center justify-center overflow-hidden"
                    >
                      {group.smallLogo || group.logo ? (
                        <img
                          src={group.smallLogo || group.logo || ""}
                          alt={group.name}
                          className="h-full w-full min-h-0 min-w-0 object-contain"
                          onError={(e) => {
                            if (
                              group.smallLogo &&
                              group.logo &&
                              e.currentTarget.src !== group.logo
                            ) {
                              e.currentTarget.src = group.logo;
                            } else {
                              e.currentTarget.style.display = "none";
                              e.currentTarget.nextElementSibling?.classList.remove(
                                "hidden"
                              );
                            }
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-full h-full bg-gray-100 rounded flex items-center justify-center ${
                          group.smallLogo || group.logo ? "hidden" : ""
                        }`}
                      >
                        <span className="text-xs text-gray-500 text-center px-1">
                          {group.name}
                        </span>
                      </div>
                    </div>
                  ))
                : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EventCardWithCatalog: React.FC<
  Omit<EventCardProps, "firmData" | "groupData">
> = (props) => {
  const { firmData } = UseFirmList();
  const { groupData } = UseGroupList();
  return <EventCardView {...props} firmData={firmData} groupData={groupData} />;
};

const EventCard: React.FC<EventCardProps> = (props) => {
  if (props.firmData !== undefined && props.groupData !== undefined) {
    return (
      <EventCardView
        {...props}
        firmData={props.firmData}
        groupData={props.groupData}
      />
    );
  }

  return <EventCardWithCatalog {...props} />;
};

export default EventCard;
